// The energy page's cards. Each one draws a slice of the report that
// core/devices/energyAnalysis.js computed — no maths here beyond formatting.

import React from 'react';
import {
  Box, Card, Chip, Table, TableBody, TableCell, TableHead, TableRow, Tooltip as MuiTooltip, Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Bar, BarChart, CartesianGrid, Legend, Line, LineChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { V_HIGH, V_LOW } from '../../../core/devices/energyAnalysis';
import {
  PHASE, ago, dateRange, duration, hourLabel, kwh, rupees, rupees2, shortDate, watts,
} from './energyFormat';

export function Section({
  title, icon, right, children, sx,
}) {
  return (
    <Card sx={{
      p: 1.5, borderRadius: 3, border: 1, borderColor: 'divider', bgcolor: 'background.paper', ...sx,
    }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
        {icon}
        <Typography fontWeight={700} sx={{ flex: 1, minWidth: 0 }}>{title}</Typography>
        {right}
      </Box>
      {children}
    </Card>
  );
}

export function Stat({
  label, value, sub, color,
}) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{label}</Typography>
      <Typography fontWeight={700} sx={{ fontSize: 20, lineHeight: 1.25, color: color || 'text.primary' }}>{value}</Typography>
      {sub && <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{sub}</Typography>}
    </Box>
  );
}

const ordinal = (n) => {
  const s = (n % 100 >= 11 && n % 100 <= 13) ? 'th' : ({ 1: 'st', 2: 'nd', 3: 'rd' }[n % 10] || 'th');
  return `${n}${s}`;
};

const statGrid = { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 1.5 };

function useChartColors() {
  const theme = useTheme();
  return {
    axis: theme.palette.text.secondary,
    grid: theme.palette.divider,
    tooltip: {
      contentStyle: {
        background: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 8,
        fontSize: 12,
      },
      labelStyle: { color: theme.palette.text.secondary },
      itemStyle: { color: theme.palette.text.primary },
    },
  };
}

// ── Live ───────────────────────────────────────────────────────────────────

export function LiveStrip({ live, phases, lastSeen }) {
  const fresh = live && live.at && Date.now() - live.at < 3 * 60000;
  return (
    <Section
      title="Live"
      right={(
        <Chip
          size="small"
          color={fresh ? 'success' : 'default'}
          label={fresh ? `Reading ${ago(live.at)}` : `Silent — last heard ${ago(lastSeen)}`}
        />
      )}
    >
      <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${phases.length}, minmax(0, 1fr))`, gap: 1 }}>
        {phases.map((p) => {
          const x = fresh ? live.phases[p] : null;
          const vBad = x && x.v > 0 && (x.v > V_HIGH || x.v < V_LOW);
          return (
            <Box key={p} sx={{ borderLeft: 4, borderColor: PHASE[p].color, pl: 1, minWidth: 0 }}>
              <Typography variant="caption" color="text.secondary">{phases.length > 1 ? PHASE[p].name : 'Supply'}</Typography>
              <Typography fontWeight={700} sx={{ fontSize: 18 }}>{x ? watts(x.w) : '—'}</Typography>
              <Typography variant="caption" sx={{ display: 'block', color: vBad ? 'warning.main' : 'text.secondary' }}>
                {x ? `${x.v ? `${x.v.toFixed(0)} V · ` : ''}${x.a.toFixed(2)} A${x.pf != null ? ` · PF ${x.pf.toFixed(2)}` : ''}` : ''}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Section>
  );
}

// ── The bill ───────────────────────────────────────────────────────────────

export function BillCard({
  period, bill, projected, projectedBill, report, settings,
}) {
  const est = report.estimatedKwh;
  return (
    <Section
      title={`Bill period · ${dateRange(period.start, period.end)}`}
      right={<Chip size="small" label={`Meter read on the ${ordinal(settings.cycleDay)}`} />}
    >
      <Box sx={statGrid}>
        <Stat
          label="Units so far"
          value={kwh(report.recordedKwh + est, 0)}
          sub={est >= 0.5 ? `${kwh(report.recordedKwh, 0)} measured + ${kwh(est, 0)} estimated for gaps` : 'all measured'}
        />
        <Stat label="Bill so far" value={rupees(bill.total)} sub="on the formula above" />
        <Stat
          label="Projected units"
          value={kwh(projected.total, 0)}
          sub={`${kwh(projected.avgWorkDay, 1)} per work day, ${kwh(projected.avgOffDay, 1)} per off day`}
        />
        <Stat label="Projected bill" value={rupees(projectedBill.total)} color="success.main" sub={`pay ${rupees(projectedBill.payPrompt)} by the prompt date`} />
      </Box>

      <Table size="small" sx={{ mt: 1.5, '& td, & th': { px: 0.75 } }}>
        <TableHead>
          <TableRow>
            <TableCell>Charge</TableCell>
            <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Formula</TableCell>
            <TableCell align="right">So far</TableCell>
            <TableCell align="right">Projected</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {bill.lines.map((l, i) => (
            <TableRow key={l.key}>
              <TableCell>
                {l.label}
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{l.local}</Typography>
              </TableCell>
              <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' }, color: 'text.secondary', fontSize: 12 }}>{l.formula}</TableCell>
              <TableCell align="right">{rupees2(l.amount)}</TableCell>
              <TableCell align="right">{rupees2(projectedBill.lines[i].amount)}</TableCell>
            </TableRow>
          ))}
          <TableRow>
            <TableCell sx={{ fontWeight: 700 }}>Bill</TableCell>
            <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }} />
            <TableCell align="right" sx={{ fontWeight: 700 }}>{rupees2(bill.total)}</TableCell>
            <TableCell align="right" sx={{ fontWeight: 700 }}>{rupees2(projectedBill.total)}</TableCell>
          </TableRow>
        </TableBody>
      </Table>

      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1.25 }}>
        <Chip size="small" label={`Pay by prompt date: ${rupees(projectedBill.payPrompt)}`} />
        <Chip size="small" label={`Online: ${rupees(projectedBill.payDigital)}`} />
        <Chip size="small" label={`On due date: ${rupees(projectedBill.payNormal)}`} />
        <Chip size="small" variant="outlined" color="warning" label={`Late: ${rupees(projectedBill.payLate)}`} />
      </Box>
    </Section>
  );
}

// ── Daily units ────────────────────────────────────────────────────────────

export function DailyChart({ report, title = 'Units per day' }) {
  const c = useChartColors();
  const data = report.daily.map((x) => {
    const row = { d: shortDate(x.d), type: x.type, est: x.estKwh >= 0.05 ? +x.estKwh.toFixed(2) : 0 };
    report.phases.forEach((p) => { row[`p${p}`] = +x.kwh[p].toFixed(2); });
    return row;
  });
  const off = report.daily.filter((x) => x.type === 'off').map((x) => shortDate(x.d));
  return (
    <Section title={title} right={<Typography variant="caption" color="text.secondary">off days marked ▲</Typography>}>
      <ResponsiveContainer width="100%" height={220}>
        <BarChart data={data} margin={{ top: 6, right: 6, bottom: 0, left: -12 }}>
          <CartesianGrid stroke={c.grid} strokeDasharray="3 3" vertical={false} />
          <XAxis
            dataKey="d"
            tick={({ x, y, payload }) => (
              <text x={x} y={y + 12} textAnchor="middle" fill={c.axis} fontSize={10}>
                {off.includes(payload.value) ? `▲${payload.value}` : payload.value}
              </text>
            )}
            stroke={c.grid}
            minTickGap={8}
          />
          <YAxis tick={{ fill: c.axis, fontSize: 10 }} stroke={c.grid} width={44} unit="" />
          <Tooltip {...c.tooltip} formatter={(v, n) => [`${v} kWh`, n]} />
          {report.phases.length > 1 && <Legend wrapperStyle={{ fontSize: 11 }} />}
          {report.phases.map((p) => (
            <Bar key={p} dataKey={`p${p}`} stackId="u" name={PHASE[p].name} fill={PHASE[p].color} isAnimationActive={false} />
          ))}
          <Bar dataKey="est" stackId="u" name="Estimated (gap)" fill={c.grid} isAnimationActive={false} />
        </BarChart>
      </ResponsiveContainer>
    </Section>
  );
}

// ── Hour-by-hour profile ───────────────────────────────────────────────────

export function ProfileChart({ report, settings }) {
  const c = useChartColors();
  const [kind, setKind] = React.useState('work');
  const rows = report.profile[kind];
  const hasOff = report.profile.off.some((r) => r.has);
  return (
    <Section
      title="Average load by hour"
      right={hasOff && (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Chip size="small" label="Work days" color={kind === 'work' ? 'primary' : 'default'} onClick={() => setKind('work')} />
          <Chip size="small" label="Off days" color={kind === 'off' ? 'primary' : 'default'} onClick={() => setKind('off')} />
        </Box>
      )}
    >
      <ResponsiveContainer width="100%" height={210}>
        <LineChart data={rows.map((r) => ({ ...r, t: hourLabel(r.h) }))} margin={{ top: 6, right: 6, bottom: 0, left: -6 }}>
          <CartesianGrid stroke={c.grid} strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="t" tick={{ fill: c.axis, fontSize: 10 }} stroke={c.grid} interval={2} />
          <YAxis tick={{ fill: c.axis, fontSize: 10 }} stroke={c.grid} width={48} tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(1)}k` : v)} />
          <Tooltip {...c.tooltip} formatter={(v, n) => [watts(v), n]} />
          <ReferenceLine x={hourLabel(settings.workStart)} stroke={c.axis} strokeDasharray="4 4" />
          <ReferenceLine x={hourLabel(settings.workEnd % 24)} stroke={c.axis} strokeDasharray="4 4" />
          {report.phases.map((p) => (
            <Line key={p} type="monotone" dataKey={`p${p}`} name={PHASE[p].name} stroke={PHASE[p].color} strokeWidth={2} dot={false} isAnimationActive={false} connectNulls />
          ))}
        </LineChart>
      </ResponsiveContainer>
      <Typography variant="caption" color="text.secondary">
        {report.opensAt != null
          ? `On work days the load rises at about ${hourLabel(report.opensAt)} and falls back by ${hourLabel(report.closesAt)}. `
          : ''}
        Dashed lines: your working hours ({hourLabel(settings.workStart)}–{hourLabel(settings.workEnd % 24)}).
      </Typography>
    </Section>
  );
}

// ── Insights ───────────────────────────────────────────────────────────────

export function WasteCard({ report, settings }) {
  const w = report.waste;
  return (
    <Section title="Waste — power used when nobody is working">
      <Box sx={statGrid}>
        <Stat label="Always-on load" value={watts(report.baseW)} sub={`night base, ≈ ${rupees(w.baseRupeesYear)} a year`} />
        <Stat label="Outside work hours" value={kwh(report.offHoursKwh, 0)} sub={`incl. off days · ≈ ${rupees(w.offHoursRupeesYear)} a year`} />
        <Stat label="On off days" value={kwh(report.offDayKwh, 1)} sub={settings.offDays.length ? 'whole days' : 'no off day set'} />
        <Stat label="Each extra unit" value={rupees2(w.rate)} sub="all taxes, top slab" />
      </Box>
    </Section>
  );
}

export function BalanceCard({ report }) {
  if (report.phases.length < 2) return null;
  const total = report.phases.reduce((a, p) => a + report.byPhase[p], 0) || 1;
  const bad = report.imbalancePct >= 30;
  return (
    <Section
      title="Phase balance"
      right={<Chip size="small" color={bad ? 'warning' : 'success'} label={`${report.imbalancePct}% imbalance`} />}
    >
      {report.phases.map((p) => {
        const share = report.byPhase[p] / total;
        return (
          <Box key={p} sx={{ mb: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
              <Typography variant="body2">{PHASE[p].name}</Typography>
              <Typography variant="body2" color="text.secondary">
                {`${kwh(report.byPhase[p], 0)} · ${Math.round(share * 100)}% · peak ${report.peakA[p].a.toFixed(1)} A`}
              </Typography>
            </Box>
            <Box sx={{ height: 8, borderRadius: 4, bgcolor: 'action.hover', overflow: 'hidden' }}>
              <Box sx={{ width: `${share * 100}%`, height: '100%', bgcolor: PHASE[p].color }} />
            </Box>
          </Box>
        );
      })}
      <Typography variant="caption" color="text.secondary">
        {bad
          ? 'One phase carries much more of the working-hour load than the others. An electrician can move a big load (an AC, a heater) to the lightest phase.'
          : 'The working-hour load is spread evenly enough across the phases.'}
      </Typography>
    </Section>
  );
}

export function VoltageCard({ report }) {
  const any = report.phases.some((p) => report.voltage[p].avg != null);
  if (!any) return null;
  return (
    <Section title="Supply voltage" right={<Typography variant="caption" color="text.secondary">normal {V_LOW}–{V_HIGH} V</Typography>}>
      <Table size="small" sx={{ '& td, & th': { px: 0.75 } }}>
        <TableHead>
          <TableRow>
            <TableCell>Phase</TableCell>
            <TableCell align="right">Min</TableCell>
            <TableCell align="right">Avg</TableCell>
            <TableCell align="right">Max</TableCell>
            <TableCell align="right">Hours high / low</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {report.phases.map((p) => {
            const v = report.voltage[p];
            return (
              <TableRow key={p}>
                <TableCell><Box component="span" sx={{ color: PHASE[p].color, fontWeight: 700 }}>{PHASE[p].label}</Box></TableCell>
                <TableCell align="right">{v.min != null ? v.min.toFixed(0) : '—'}</TableCell>
                <TableCell align="right">{v.avg != null ? v.avg.toFixed(0) : '—'}</TableCell>
                <TableCell align="right" sx={{ color: v.max > V_HIGH ? 'warning.main' : undefined }}>{v.max != null ? v.max.toFixed(0) : '—'}</TableCell>
                <TableCell align="right" sx={{ color: v.hoursHigh + v.hoursLow ? 'warning.main' : undefined }}>
                  {`${v.hoursHigh} / ${v.hoursLow}`}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      {report.phases.some((p) => report.voltage[p].hoursHigh > 0) && (
        <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'warning.main' }}>
          Over-voltage: some hours averaged above {V_HIGH} V. It shortens the life of lights, motors and electronics — worth a complaint to the supplier, with this table as evidence.
        </Typography>
      )}
    </Section>
  );
}

export function PowerFactorCard({ report }) {
  const vals = report.phases.map((p) => report.powerFactor[p]).filter((x) => x != null);
  if (!vals.length) return null;
  return (
    <Section title="Power factor (while loaded)">
      <Box sx={statGrid}>
        {report.phases.map((p) => {
          const pf = report.powerFactor[p];
          return (
            <Stat
              key={p}
              label={report.phases.length > 1 ? PHASE[p].name : 'Supply'}
              value={pf != null ? pf.toFixed(2) : '—'}
              color={pf != null && pf < 0.85 ? 'warning.main' : undefined}
              sub={pf == null ? 'no load' : pf < 0.85 ? 'low — inductive load' : pf < 0.95 ? 'fair' : 'good'}
            />
          );
        })}
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        Watts ÷ (volts × amps). Below about 0.9 the wiring carries current that does no work; motors and AC compressors cause it, a capacitor bank corrects it.
      </Typography>
    </Section>
  );
}

export function PeakCard({ report, settings }) {
  const pk = report.peak;
  const kw = settings.sanctionedKw;
  const pct = pk && kw ? Math.round((pk.w / 1000 / kw) * 100) : null;
  return (
    <Section title="Peak demand">
      <Box sx={statGrid}>
        <Stat label="Highest total load" value={pk ? watts(pk.w) : '—'} sub={pk ? `${shortDate(pk.at.slice(0, 10))} at ${pk.at.slice(11, 16)}` : ''} />
        <Stat
          label="Sanctioned load"
          value={kw ? `${kw} kW` : 'not set'}
          sub={pct != null ? `peak uses ${pct}% of it` : 'set it in the tariff box'}
          color={pct != null && pct > 90 ? 'warning.main' : undefined}
        />
        {report.phases.map((p) => (
          <Stat key={p} label={`Peak current ${report.phases.length > 1 ? PHASE[p].label : ''}`} value={`${report.peakA[p].a.toFixed(1)} A`} sub={report.peakA[p].d ? shortDate(report.peakA[p].d) : ''} />
        ))}
      </Box>
    </Section>
  );
}

export function QualityCard({ report }) {
  const big = [...report.gaps].sort((a, b) => b.missingS - a.missingS);
  return (
    <Section
      title="Data quality"
      right={<Chip size="small" color={report.coveragePct >= 97 ? 'success' : 'warning'} label={`${report.coveragePct}% covered`} />}
    >
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {report.gaps.length
          ? `${report.gaps.length} gap${report.gaps.length > 1 ? 's' : ''} in the readings. The units they swallowed are estimated from the same hours on similar days: ${kwh(report.estimatedKwh, 1)}.`
          : 'No gaps — every hour has readings.'}
      </Typography>
      {big.slice(0, 8).map((g) => (
        <Box key={g.from} sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, py: 0.25 }}>
          <Typography variant="body2">{`${shortDate(g.from.slice(0, 10))} ${g.from.slice(11)} → ${g.to.slice(0, 10) === g.from.slice(0, 10) ? g.to.slice(11) : `${shortDate(g.to.slice(0, 10))} ${g.to.slice(11)}`}`}</Typography>
          <Typography variant="body2" color="text.secondary">{duration(g.missingS)} missing</Typography>
        </Box>
      ))}
      {big.length > 8 && <Typography variant="caption" color="text.secondary">{`… and ${big.length - 8} shorter`}</Typography>}
    </Section>
  );
}

export function Findings({ items }) {
  if (!items.length) return null;
  return (
    <Section title="What the readings say">
      <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
        {items.map((t) => (
          <Typography component="li" variant="body2" key={t} sx={{ mb: 0.5 }}>{t}</Typography>
        ))}
      </Box>
    </Section>
  );
}

export function SourceNote({ source }) {
  if (source !== 'raw') return null;
  return (
    <MuiTooltip title="GetCurrentSenseHourly (backend energy.js) is not deployed yet, so each day is downloaded in full and summarised on this device. Finished days are cached.">
      <Chip size="small" variant="outlined" label="Slow mode — summary endpoint not deployed" />
    </MuiTooltip>
  );
}
